import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchPurchaseById,
  fetchPurchasePayments,
  archivePurchaseRpc,
  unarchivePurchaseRpc,
} from '@/services/purchases';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Purchase } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { PurchasePaymentModal } from './PurchasePaymentModal';
import { PurchaseCancelModal } from './PurchaseCancelModal';
import { PurchaseCorrectionModal } from './PurchaseCorrectionModal';
import { ArchiveRecordModal } from '@/components/common/ArchiveRecordModal';
import { useAuth } from '@/context/AuthContext';
import {
  ShoppingBag,
  DollarSign,
  Package,
  Laptop as LaptopIcon,
  CreditCard,
  Loader2,
  Ban,
  Archive,
  RotateCcw,
  Edit3,
} from 'lucide-react';

interface PurchaseDetailProps {
  purchaseId: string;
  onClose: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const PurchaseDetail: React.FC<PurchaseDetailProps> = ({ purchaseId, onClose }) => {
  const { hasPermission, role } = useAuth();
  const isOwner = role === 'owner';
  const isManagerOrOwner = hasPermission('purchases');
  const queryClient = useQueryClient();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [unarchiveLoading, setUnarchiveLoading] = useState(false);
  const [unarchiveError, setUnarchiveError] = useState<string | null>(null);

  const { data: purchase, isLoading, error, refetch } = useQuery<Purchase>({
    queryKey: queryKeys.purchases.detail(purchaseId),
    queryFn: () => fetchPurchaseById(purchaseId),
  });

  const { data: payments = [] } = useQuery({
    queryKey: queryKeys.purchases.payments(purchaseId),
    queryFn: () => fetchPurchasePayments(purchaseId),
    enabled: !!purchase,
  });

  const isCancelled = purchase?.status === 'cancelled';
  const isArchived = !!purchase?.archived_at;
  const balanceDue = purchase && !isCancelled ? Math.max(0, purchase.total - (purchase.amount_paid || 0)) : 0;

  const handleUnarchive = async () => {
    if (!purchase) return;
    try {
      setUnarchiveLoading(true);
      setUnarchiveError(null);
      await unarchivePurchaseRpc(purchase.id);
      void queryClient.invalidateQueries({ queryKey: queryKeys.purchases.all });
      void refetch();
    } catch (err: unknown) {
      setUnarchiveError(parseDatabaseError(err));
    } finally {
      setUnarchiveLoading(false);
    }
  };

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}
      {unarchiveError && <ErrorAlert message={unarchiveError} />}

      {purchase && !isLoading && (
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
          {/* Archived Banner */}
          {isArchived && (
            <div className="p-3 bg-slate-100 border border-slate-300 rounded-lg flex items-start justify-between gap-2 text-xs text-slate-800">
              <div className="flex items-start space-x-2">
                <Archive className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold uppercase tracking-wider block">Archived Purchase (Hidden from Active Operations)</span>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Reason: <em>{purchase.archive_reason || 'No explanation provided'}</em> • Archived on {formatDate(purchase.archived_at!)}
                  </p>
                </div>
              </div>
              {isOwner && (
                <button
                  type="button"
                  onClick={handleUnarchive}
                  disabled={unarchiveLoading}
                  className="inline-flex items-center space-x-1 px-2.5 py-1 bg-white border border-slate-300 text-slate-700 text-xs font-semibold rounded hover:bg-slate-50 transition-colors shrink-0"
                >
                  {unarchiveLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  <span>Restore</span>
                </button>
              )}
            </div>
          )}

          {/* Cancelled Banner */}
          {isCancelled && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2 text-xs text-red-900">
              <Ban className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
              <div>
                <p className="font-bold uppercase tracking-wider">Purchase Voided / Cancelled</p>
                <p className="text-[11px] text-red-800 mt-0.5">
                  This purchase was cancelled by shop management. Inventory items were reversed or marked as scrapped.
                </p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 pb-3 border-b border-brand-border">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-lg bg-brand-green/10 flex items-center justify-center text-brand-green shrink-0">
                <ShoppingBag className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                  <span className="font-bold text-sm text-brand-black font-mono">
                    {purchase.purchase_number}
                  </span>
                  {purchase.supplier_invoice_number && (
                    <span className="text-xs text-brand-muted font-normal">
                      (Ref: {purchase.supplier_invoice_number})
                    </span>
                  )}
                  {isArchived && (
                    <span className="px-2 py-0.5 bg-slate-200 text-slate-800 text-xs font-bold rounded-full uppercase">
                      Archived
                    </span>
                  )}
                  <span
                    className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                      isCancelled
                        ? 'bg-red-100 text-red-800 font-bold uppercase'
                        : purchase.payment_status === 'paid'
                        ? 'bg-green-100 text-green-800'
                        : purchase.payment_status === 'partial'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {isCancelled ? 'Cancelled' : purchase.payment_status}
                  </span>
                </div>
                <p className="text-xs text-brand-muted mt-0.5">
                  Supplier: <strong className="text-brand-black">{purchase.supplier?.name || 'N/A'}</strong> • {formatDate(purchase.purchase_date)}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 flex-wrap gap-y-1.5">
              {!isCancelled && balanceDue > 0 && (
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(true)}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-brand-green text-white text-xs font-semibold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
                >
                  <DollarSign className="w-3.5 h-3.5" />
                  <span>Record Payment</span>
                </button>
              )}

              {!isCancelled && isManagerOrOwner && (
                <button
                  type="button"
                  onClick={() => setShowCorrectionModal(true)}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-amber-50 text-amber-900 border border-amber-300 text-xs font-semibold rounded-md hover:bg-amber-100 transition-colors"
                  title="Edit or correct purchase metadata (supplier reference, date, notes)"
                >
                  <Edit3 className="w-3.5 h-3.5 text-amber-600" />
                  <span>Edit Purchase</span>
                </button>
              )}

              {!isCancelled && isManagerOrOwner && (
                <button
                  type="button"
                  onClick={() => setShowCancelModal(true)}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-red-50 text-brand-red border border-red-200 text-xs font-semibold rounded-md hover:bg-red-100 transition-colors"
                  title="Cancel / Void this purchase and reverse inventory"
                >
                  <Ban className="w-3.5 h-3.5" />
                  <span>Void Purchase</span>
                </button>
              )}

              {isOwner && !isArchived && (
                <button
                  type="button"
                  onClick={() => setShowArchiveModal(true)}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-slate-100 text-slate-700 border border-slate-300 text-xs font-semibold rounded-md hover:bg-slate-200 transition-colors"
                  title="Archive / Hide this test or legacy purchase record"
                >
                  <Archive className="w-3.5 h-3.5" />
                  <span>Archive Record</span>
                </button>
              )}
            </div>
          </div>

          {/* Financial Breakdown */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 bg-brand-bg p-3 rounded-lg border border-brand-border text-xs">
            <div>
              <p className="font-semibold text-brand-muted uppercase text-[10px]">Items Subtotal</p>
              <p className="font-bold text-brand-black">{formatCurrency(purchase.subtotal)}</p>
            </div>
            <div>
              <p className="font-semibold text-brand-muted uppercase text-[10px]">Shipping</p>
              <p className="font-bold text-brand-black">{formatCurrency(purchase.shipping_cost || 0)}</p>
            </div>
            <div>
              <p className="font-semibold text-brand-muted uppercase text-[10px]">Other Costs</p>
              <p className="font-bold text-brand-black">{formatCurrency(purchase.other_cost || 0)}</p>
            </div>
            <div>
              <p className="font-semibold text-brand-muted uppercase text-[10px]">Purchase Discount</p>
              <p className="font-bold text-amber-700">- {formatCurrency(purchase.discount || 0)}</p>
            </div>
            <div>
              <p className="font-semibold text-brand-muted uppercase text-[10px]">Net Purchase Total</p>
              <p className="font-extrabold text-brand-green">{formatCurrency(purchase.total)}</p>
            </div>
            <div>
              <p className="font-semibold text-brand-muted uppercase text-[10px]">Amount Paid</p>
              <p className="font-bold text-brand-black">{formatCurrency(purchase.amount_paid || 0)}</p>
            </div>
            <div>
              <p className="font-semibold text-brand-muted uppercase text-[10px]">Balance</p>
              <p className={`font-bold ${balanceDue > 0 ? 'text-brand-red' : 'text-brand-green'}`}>{formatCurrency(balanceDue)}</p>
            </div>
          </div>

          {/* Items Received List */}
          {purchase.items && purchase.items.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-brand-black uppercase tracking-wider mb-2">Items Received</h4>
              <div className="bg-white border border-brand-border rounded-lg overflow-hidden text-xs">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold">
                    <tr>
                      <th className="text-left px-3 py-2">Item Type / Details</th>
                      <th className="text-left px-3 py-2">Qty</th>
                      <th className="text-left px-3 py-2">Unit Cost</th>
                      <th className="text-left px-3 py-2">Landed Unit Cost</th>
                      <th className="text-right px-3 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {purchase.items.map((item) => (
                      <tr key={item.id}>
                        <td className="px-3 py-2 font-medium text-brand-black">
                          {item.item_type === 'laptop' ? (
                            <span className="flex items-center space-x-1.5">
                              <LaptopIcon className="w-3.5 h-3.5 text-brand-green shrink-0" />
                              <span>{item.laptop ? `${item.laptop.stock_id} - ${item.laptop.brand} ${item.laptop.model}` : 'Serialized Laptop'}</span>
                            </span>
                          ) : (
                            <span className="flex items-center space-x-1.5">
                              <Package className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                              <span>{item.accessory ? item.accessory.name : 'Accessory Line'}</span>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">{item.quantity}</td>
                        <td className="px-3 py-2">{formatCurrency(item.unit_cost)}</td>
                        <td className="px-3 py-2 text-brand-muted">{formatCurrency(item.allocated_cost)}</td>
                        <td className="px-3 py-2 text-right font-bold text-brand-black">{formatCurrency(item.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payment History */}
          {payments.length > 0 && (
            <div className="pt-2 border-t border-brand-border">
              <h4 className="text-xs font-bold text-brand-black uppercase tracking-wider mb-2 flex items-center space-x-1">
                <CreditCard className="w-3.5 h-3.5 text-brand-green" />
                <span>Payment History</span>
              </h4>
              <div className="space-y-2">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-xs p-2 bg-brand-bg rounded border border-brand-border">
                    <div>
                      <span className="font-semibold text-brand-green">Rs. {p.amount.toLocaleString('en-PK')}</span>
                      <span className="text-brand-muted"> via {p.payment_method}</span>
                      {p.notes && <p className="text-[11px] text-brand-muted">{p.notes}</p>}
                    </div>
                    <span className="text-[10px] text-brand-muted shrink-0">{formatDate(p.payment_date || p.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {purchase.notes && (
            <div className="p-3 bg-brand-bg rounded border border-brand-border text-xs">
              <p className="font-semibold text-brand-black mb-0.5">Purchase Notes</p>
              <p className="text-brand-muted break-words">{purchase.notes}</p>
            </div>
          )}

          {showPaymentModal && (
            <PurchasePaymentModal
              purchase={purchase}
              isOpen={showPaymentModal}
              onClose={() => {
                setShowPaymentModal(false);
                void refetch();
              }}
            />
          )}

          {showCancelModal && (
            <PurchaseCancelModal
              purchase={purchase}
              isOpen={showCancelModal}
              onClose={() => setShowCancelModal(false)}
              onSuccess={() => {
                setShowCancelModal(false);
                void refetch();
              }}
            />
          )}

          {showArchiveModal && (
            <ArchiveRecordModal
              isOpen={showArchiveModal}
              onClose={() => setShowArchiveModal(false)}
              entityType="Purchase"
              identifier={purchase.purchase_number}
              subIdentifier={`Supplier: ${purchase.supplier?.name || 'N/A'} • ${formatDate(purchase.purchase_date)}`}
              onConfirm={async (reason) => {
                await archivePurchaseRpc({ purchaseId: purchase.id, reason });
                setShowArchiveModal(false);
                void queryClient.invalidateQueries({ queryKey: queryKeys.purchases.all });
                void refetch();
              }}
            />
          )}

          {showCorrectionModal && (
            <PurchaseCorrectionModal
              purchase={purchase}
              isOpen={showCorrectionModal}
              onClose={() => {
                setShowCorrectionModal(false);
                void refetch();
              }}
            />
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end pt-3 border-t border-brand-border">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors">
          Close
        </button>
      </div>
    </>
  );
};
